import { useState, useEffect } from "react";
import api from "../../../services/api";

interface MetricCardProps {
  title: string;
  value: string | number;
  label: string;
  icon: string;
  colorClass: string;
  bgClass: string;
  trend: string;
  trendUp: boolean;
}

function MetricCard({ title, value, label, icon, colorClass, bgClass, trend, trendUp }: MetricCardProps) {
  return (
    <div className="bg-surface-container-low p-6 rounded-[2rem] border border-outline-variant/10 shadow-sm hover:shadow-2xl hover:translate-y-[-4px] transition-all group flex flex-col justify-between h-44 relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-24 h-24 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl opacity-20 ${bgClass}`} />
      
      <div className="flex justify-between items-start relative">
        <div className={`w-12 h-12 rounded-2xl ${bgClass} ${colorClass} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
          <span className="material-symbols-outlined text-2xl fill-1">{icon}</span>
        </div>
        <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-tight px-2 py-1 rounded-full ${trendUp ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
          <span className="material-symbols-outlined text-[12px]">{trendUp ? 'trending_up' : 'trending_down'}</span>
          {trend}
        </div>
      </div>

      <div className="relative">
        <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">{title}</p>
        <div className="flex items-baseline gap-2">
          <h4 className="text-3xl font-black text-on-surface tracking-tighter">{value}</h4>
          <span className="text-[10px] font-bold text-on-surface-variant/60">{label}</span>
        </div>
      </div>
    </div>
  );
}

export default function MarketingMetrics() {
  const [metrics, setMetrics] = useState({
    rating: "4.9",
    totalCompleted: 0,
    monthlyThroughput: 0,
    poolBacklog: 0
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await api.get(`/service-pool?t=${Date.now()}`);
        const pool = Array.isArray(res.data) ? res.data : [];
        
        const completions = pool.filter((i: any) => i.type === "EXPERT_JOB" && i.status === "COMPLETED");
        const backlog = pool.filter((i: any) => i.type === "EXPERT_JOB" && i.status === "OPEN").length;
        
        // Calculate monthly flow (last 30 days)
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        const monthly = completions.filter((i: any) => new Date(i.createdAt) > thirtyDaysAgo).length;

        setMetrics({
          rating: (4.7 + (Math.random() * 0.3)).toFixed(1), // Simulated live rating
          totalCompleted: completions.length,
          monthlyThroughput: monthly,
          poolBacklog: backlog
        });
      } catch (err) {
        console.error("Failed to fetch marketing metrics:", err);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard 
        title="Quality Score"
        value={metrics.rating}
        label="/ 5.0"
        icon="star_rate"
        colorClass="text-amber-500"
        bgClass="bg-amber-500/10"
        trend="+0.2"
        trendUp={true}
      />
      <MetricCard 
        title="Assets Delivered"
        value={metrics.totalCompleted}
        label="Total"
        icon="verified"
        colorClass="text-emerald-500"
        bgClass="bg-emerald-500/10"
        trend="+14"
        trendUp={true}
      />
      <MetricCard 
        title="Monthly Flow"
        value={metrics.monthlyThroughput}
        label="Units"
        icon="speed"
        colorClass="text-purple-500"
        bgClass="bg-purple-500/10"
        trend="+8%"
        trendUp={true}
      />
      <MetricCard 
        title="Job Backlog"
        value={metrics.poolBacklog}
        label="Open"
        icon="engineering"
        colorClass="text-orange-500"
        bgClass="bg-orange-500/10"
        trend="-4"
        trendUp={true}
      />
    </div>
  );
}
